import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { forkJoin, Observable } from 'rxjs';

import { ApiHttp, SCHEMA_VERSION } from '@core/api/api-http';
import { ContactSummary } from '@features/contacts/models/contactsummary';
import { environment } from '@env/environment';

export interface ContactSearchFilterOptions {
  search?: string;
  contactType?: string;
  ownedBy?: string;
  /** When set, list is filtered client-side to contacts returned for this tag from the Clients tags API. */
  tagId?: string;
}

function getSearchParams(options: ContactSearchFilterOptions) {
  let params: any = {};
  if (options.search) {
    params.search = options.search;
  }

  if (options.contactType) {
    params.type = options.contactType;
  }

  if (options.ownedBy) {
    params.ownedBy = options.ownedBy.trim();
  }

  return Object.keys(params) ? params : null;
}

function getSelectParams(ids: string[]): any {
  return {
    ids: ids.join(',')
  };
}

@Injectable({
  providedIn: 'root'
})
export class ContactService {
  constructor(private http: ApiHttp) {}

  getContacts(
    options: ContactSearchFilterOptions
  ): Observable<ContactSummary[]> {
    const params = getSearchParams(options);
    let httpOptions: any;
    if (params) {
      httpOptions = {
        headers: ApiHttp.getStandardOptions().headers,
        params
      };
    } else {
      httpOptions = ApiHttp.getStandardOptions();
    }
    return this.http.get(environment.clientsApiUrl, 'contacts', httpOptions);
  }

  getSpecificContacts(ids: string[]): Observable<ContactSummary[]> {
    const params = getSelectParams(ids);
    let httpOptions = {
      headers: ApiHttp.getStandardOptions().headers,
      params
    };
    return this.http.getEntity(
      environment.clientsApiUrl,
      'individuals/select',
      SCHEMA_VERSION.INDIVIDUAL,
      httpOptions
    );
  }

  getPeople(search: string): Observable<ContactSummary[]> {
    if (search.length > 0) {
      var httpOptions = {
        headers: ApiHttp.getStandardOptions().headers,
        params: { search: search }
      };
      return this.http.getEntity(
        environment.clientsApiUrl,
        'individuals',
        SCHEMA_VERSION.INDIVIDUAL,
        httpOptions
      );
    } else {
      return this.http.getEntity(
        environment.clientsApiUrl,
        'individuals',
        SCHEMA_VERSION.INDIVIDUAL,
        ApiHttp.getStandardOptions()
      );
    }
  }

  getReferrers(search: string): Observable<ContactSummary[]> {
    var httpOptions = {
      headers: ApiHttp.getStandardOptions().headers,
      params: { search: search, type: 'Referrer' }
    };
    return this.http.get(environment.clientsApiUrl, 'contacts', httpOptions);
  }

  getCompanies() {
    return this.http.getEntity(
      environment.clientsApiUrl,
      'companies',
      SCHEMA_VERSION.COMPANY,
      ApiHttp.getStandardOptions()
    );
  }

  reassign(portfolioId: string, contactSummary: ContactSummary) {
    let route = `${contactSummary.id}/reassign/${portfolioId}`;
    let entitySchemaVersion = null;
    switch (contactSummary.entityType) {
      case 'Individual':
        route = `individuals/${route}`;
        entitySchemaVersion = SCHEMA_VERSION.INDIVIDUAL;
        break;
      case 'Company':
        route = `companies/${route}`;
        entitySchemaVersion = SCHEMA_VERSION.COMPANY;
        break;
      case 'Trust':
        route = `trusts/${route}`;
        entitySchemaVersion = SCHEMA_VERSION.TRUST;
        break;
    }

    if (entitySchemaVersion != null) {
      return this.http.postEntity(
        environment.clientsApiUrl,
        route,
        {},
        entitySchemaVersion,
        ApiHttp.getStandardOptions()
      );
    }

    return this.http.post(
      environment.clientsApiUrl,
      route,
      {},
      ApiHttp.getStandardOptions()
    );
  }

  reassignAll(
    contacts: ContactSummary[],
    portfolioId: string
  ): Observable<object> {
    return forkJoin(contacts.map((c) => this.reassign(portfolioId, c)));
  }
}
